import React, { useState, useEffect } from 'react';
import ProgressBar from '@/components/shared/ProgressBar';
import { Target, GripVertical, Edit2, Trash2, ArrowUpDown, Filter, Search, CheckSquare, Square, Plus } from 'lucide-react';
import EditableField from '@/components/shared/EditableField';
import { getUrgencyStatus, getUrgencyTextColor, getUrgencyBgColor, getStatusColor, getStatusDotColor, cn, sortByColumn } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useCategory } from '@/contexts/CategoryManagementContext';

const GoalsWidget = ({ goals = [], onUpdate, dragHandleProps, onEdit, onDelete, onCreate, statusTypes = [] }) => {
  const [activeTab, setActiveTab] = useState('Aberto');
  const [sortConfig, setSortConfig] = useState({ key: 'dueDate', direction: 'asc' });
  const [filterUrgency, setFilterUrgency] = useState('all');
  const { canDeleteCards } = useAuth();
  const { getCategoryByDays } = useCategory();
  const [responsaveis, setResponsaveis] = useState([]);
  const { toast } = useToast();

  useEffect(() => {
    let mounted = true;
    const fetchResponsaveis = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const res = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000/api'}/servicos/responsaveis`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
        });
        if (!res.ok) throw new Error('Erro ao buscar responsáveis');
        const data = await res.json();
        if (mounted) setResponsaveis(data);
      } catch (err) {
        if (mounted) setResponsaveis([]);
      }
    };
    fetchResponsaveis();
    return () => { mounted = false; };
  }, []);
  // Status default fixos para metas
  const defaultStatusOptions = [
    { id: 'default-aberto', name: 'Aberto' },
    { id: 'default-concluido', name: 'Concluído' }
  ];

  // Junta os default com os customizados, sem duplicar
  const goalStatusTypes = statusTypes.filter(st => st.type === 'meta');
  const customNames = goalStatusTypes.map(st => st.name);
  const statusOptions = [
    ...defaultStatusOptions.filter(ds => !customNames.includes(ds.name)),
    ...goalStatusTypes
  ];

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleStatusChange = (goal, newStatusName) => {
    onUpdate(goal.id, 'lifecycleStatus', newStatusName, goal.name, goal.lifecycleStatus);
  };

  // Filtering
  const filteredGoals = goals.filter(g => {
    // Lifecycle Filter
    if (g.lifecycleStatus !== activeTab) return false;

    // Urgency Filter
    if (filterUrgency !== 'all') {
       const status = getUrgencyStatus(g.dueDate);
       if (filterUrgency === 'normal') return status !== 'overdue' && status !== 'urgent';
       return status === filterUrgency;
    }
    return true;
  });

  // Sorting
  const sortedGoals = sortByColumn(filteredGoals, sortConfig.key, sortConfig.direction);

  const urgencyOptions = [
    { value: 'all', label: 'Todas Urgências' },
    { value: 'overdue', label: 'Vencido (Vermelho)' },
    { value: 'urgent', label: 'Próximo (Amarelo)' },
    { value: 'on-track', label: 'No Prazo (Verde)' }
  ];

  // Status para tabs (default + customizados)
  const tabStatusOptions = [
    ...defaultStatusOptions,
    ...goalStatusTypes
  ];

  // progress display is read-only in the table; editing happens in the modal

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-white">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center shadow-md shadow-orange-500/20">
            <Target className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-lg font-bold text-black">Metas</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={onCreate} className="bg-orange-500 hover:bg-orange-600 text-white gap-1 h-8">
            <Plus className="w-4 h-4" /> Novo
          </Button>
          {dragHandleProps && (
            <div {...dragHandleProps} className="cursor-grab active:cursor-grabbing p-1.5 hover:bg-gray-50 rounded-lg text-gray-400 hover:text-orange-500 transition-colors">
              <GripVertical className="w-4 h-4" />
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="p-4 bg-gray-50/50 border-b border-gray-100 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        {/* Tabs */}
        <div className="flex bg-gray-200/50 p-1 rounded-lg">
          {tabStatusOptions.map((status) => (
            <button
              key={status.id || status.name}
              onClick={() => setActiveTab(status.name)}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                activeTab === status.name ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {status.name}
            </button>
          ))}
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select 
            value={filterUrgency}
            onChange={(e) => setFilterUrgency(e.target.value)}
            className="bg-white border border-gray-200 text-gray-700 text-xs rounded-md px-2 py-1.5 focus:border-orange-500 focus:outline-none cursor-pointer hover:border-gray-300"
          >
            {urgencyOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table/List */}
      <div className="flex-1 overflow-auto bg-white relative">
        <table className="w-full text-left border-collapse">
          <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm hidden md:table-header-group">
            <tr>
              <th className="p-3 text-xs font-bold text-gray-500 uppercase tracking-wider w-10">Status</th>
              <th className="p-3 text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-orange-600 group" onClick={() => handleSort('name')}>
                <div className="flex items-center gap-1">Meta <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-100" /></div>
              </th>
              <th className="p-3 text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-orange-600 group" onClick={() => handleSort('openingDate')}>
                 <div className="flex items-center gap-1">Abertura <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-100" /></div>
              </th>
              <th className="p-3 text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-orange-600 group" onClick={() => handleSort('dueDate')}>
                 <div className="flex items-center gap-1">Prazo <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-100" /></div>
              </th>
               <th className="p-3 text-xs font-bold text-gray-500 uppercase tracking-wider">
                 Progresso
              </th>
               <th className="p-3 text-xs font-bold text-gray-500 uppercase tracking-wider">
                 Responsável
              </th>
              <th className="p-3 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sortedGoals.length === 0 ? (
               <tr>
                 <td colSpan="7" className="p-8 text-center text-gray-400 text-sm">
                   Nenhuma meta encontrada.
                 </td>
               </tr>
            ) : (
              sortedGoals.map((goal, idx) => {
                const urgency = getUrgencyStatus(goal.dueDate);
                const urgencyColor = getUrgencyTextColor(urgency);
                const urgencyBg = getUrgencyBgColor(urgency);
                
                return (
                  <tr key={goal.id} className={cn("hover:bg-orange-50/30 transition-colors group flex flex-col md:table-row border-b md:border-none p-4 md:p-0 relative", idx % 2 === 1 ? "bg-gray-200" : "bg-white") }>
                     {/* Urgent Indicator Strip (Mobile Only) */}
                     <div className={cn("absolute left-0 top-0 bottom-0 w-1 md:hidden", urgency === 'overdue' ? 'bg-red-500' : urgency === 'urgent' ? 'bg-yellow-500' : 'bg-green-500')} />

                    <td className="p-2 md:p-3 md:w-10 flex md:table-cell items-center justify-center">
                      <span className="md:hidden text-xs font-bold text-gray-500 uppercase">Status</span>
                      <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                          <button aria-label={`Status: ${goal.lifecycleStatus}`} className="flex items-center p-1 md:p-1 rounded-md hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300">
                            {(() => {
                              const custom = statusTypes.find(st => st.name === goal.lifecycleStatus);
                              return (
                                <div className="flex items-center gap-2">
                                  <div className="w-9 h-9 flex items-center justify-center border rounded-md bg-white">
                                    {custom && custom.color ? (
                                      <span className="inline-block w-3.5 h-3.5 rounded-full" style={{ backgroundColor: custom.color, borderColor: custom.color }} />
                                    ) : (
                                      <span className={`${getStatusDotColor(goal.lifecycleStatus)} inline-block w-3.5 h-3.5 rounded-full bg-current`} />
                                    )}
                                  </div>
                                  <svg className="w-3 h-3 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd"/></svg>
                                </div>
                              );
                            })()}
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-gray-50 border border-gray-100 rounded-md shadow-sm">
                          {statusOptions.map(st => (
                            <DropdownMenuItem className="text-black" key={st.id || st.name} onClick={() => handleStatusChange(goal, st.name)}>{st.name}</DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                    
                    <td className="p-2 md:p-3 pl-4 md:pl-3">
                       <span className="md:hidden text-xs font-bold text-gray-500 uppercase block mb-1">Meta</span>
                       <div className="font-medium text-sm text-gray-900">
                          <EditableField 
                            value={goal.name}
                            onSave={(f, v, old) => onUpdate(goal.id, 'name', v, undefined, old)}
                            entityId={`goal-${goal.id}`}
                            fieldName="name"
                            className="w-full"
                          />
                       </div>
                       <details className="text-xs text-gray-400 mt-0.5">
                          <summary className="cursor-pointer select-none text-gray-500 hover:text-gray-700">Ver descrição</summary>
                          <div className="mt-1">
                            <EditableField 
                              value={goal.description || '-'}
                              onSave={(f, v, old) => onUpdate(goal.id, 'description', v, undefined, old)}
                              entityId={`goal-${goal.id}`}
                              fieldName="description"
                              className="text-gray-400 italic"
                            />
                          </div>
                       </details>
                    </td>

                    <td className="p-2 md:p-3 pl-4 md:pl-3">
                      <span className="md:hidden text-xs font-bold text-gray-500 uppercase block mb-1">Abertura</span>
                       <EditableField 
                            value={goal.openingDate}
                            onSave={(f, v, old) => onUpdate(goal.id, 'openingDate', v, undefined, old)}
                            entityId={`goal-${goal.id}`}
                            fieldName="openingDate"
                            type="date"
                            className="text-xs text-gray-600"
                          />
                    </td>

                    <td className="p-2 md:p-3 pl-4 md:pl-3">
                      <span className="md:hidden text-xs font-bold text-gray-500 uppercase block mb-1">Prazo</span>
                      <div className={cn("inline-flex items-center px-2 py-1 rounded text-xs font-bold border", urgencyBg, urgencyColor, `border-${urgencyColor.split('-')[1]}-200`)}>
                         <EditableField 
                            value={goal.dueDate}
                            onSave={(f, v, old) => onUpdate(goal.id, 'dueDate', v, undefined, old)}
                            entityId={`goal-${goal.id}`}
                            fieldName="dueDate"
                            type="date"
                          />
                      </div>
                    </td>

                    <td className="p-2 md:p-3 pl-4 md:pl-3">
                       <span className="md:hidden text-xs font-bold text-gray-500 uppercase block mb-1">Progresso</span>
                       <div className="flex flex-col gap-1">
                         <div className="flex items-center gap-2">
                           <div className="w-48">
                             <ProgressBar progress={Math.max(0, Math.min(100, ((Number(goal.current) || 0) / (Number(goal.target) || 100)) * 100))} status={goal.lifecycleStatus === 'Concluído' ? 'completed' : 'on-track'} />
                           </div>
                           <div className="text-xs font-mono text-gray-600 flex items-center gap-1">
                             <EditableField 
                               value={String(Number(goal.current) || 0)}
                               onSave={(f, v, old) => onUpdate(goal.id, 'current', v, undefined, old)}
                               entityId={`goal-${goal.id}`}
                               fieldName="current"
                               type="number"
                               className="text-gray-900 font-mono text-xs"
                             />
                             <span className="text-gray-600">/</span>
                             <EditableField 
                               value={String(Number(goal.target) || 100)}
                               onSave={(f, v, old) => onUpdate(goal.id, 'target', v, undefined, old)}
                               entityId={`goal-${goal.id}`}
                               fieldName="target"
                               type="number"
                               className="text-gray-900 font-mono text-xs"
                             />
                           </div>
                         </div>
                       </div>
                    </td>

                     <td className="p-2 md:p-3 pl-4 md:pl-3">
                       <span className="md:hidden text-xs font-bold text-gray-500 uppercase block mb-1">Responsável</span>
                        <EditableField 
                            value={goal.assignedTo || ''}
                            onSave={(f, v, old) => onUpdate(goal.id, 'assignedTo', v, undefined, old)}
                            entityId={`goal-${goal.id}`}
                            fieldName="assignedTo"
                            type="select"
                            options={responsaveis.map(r => ({ value: r.name, label: `${r.name} ${r.email ? `(${r.email})` : '' }` }))}
                            className="text-xs text-gray-600"
                          />
                    </td>

                    <td className="p-2 md:p-3 text-right">
                       <div className="flex items-center justify-end gap-1">
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-gray-400 hover:text-orange-500" onClick={() => onEdit(goal)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-gray-400 hover:text-red-500" onClick={() => onDelete(goal)} title="Excluir">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                       </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GoalsWidget;